import React, { useEffect, useState } from 'react';
import * as emotion from 'emotion';
import * as emotionCore from '@emotion/core';
import * as emotionTheming from 'emotion-theming';
import { getBodyEnd, getViewLog, logView, getWeeklyArticleHistory, } from '@guardian/automat-client';
import { isRecurringContributor, getLastOneOffContributionDate, shouldHideSupportMessaging, hasOptedOutOfArticleCount, } from '@root/src/web/lib/contributions';
import { initPerf } from '@root/src/web/browser/initPerf';
import { sendOphanComponentEvent, } from '@root/src/web/browser/ophan/ophan';
import { getCookie } from '../browser/cookie';
import { useHasBeenSeen } from '../lib/useHasBeenSeen';
const { css } = emotion;
const checkForErrors = (response) => {
    if (!response.ok) {
        throw Error(response.statusText ||
            `SlotBodyEnd | An api call returned HTTP status ${response.status}`);
    }
    return response;
};
const sendOphanReminderEvent = (componentId) => {
    const componentEvent = {
        component: {
            componentType: 'ACQUISITIONS_OTHER',
            id: componentId,
        },
        action: 'CLICK',
    };
    window.guardian.ophan.record({ componentEvent });
};
const sendOphanReminderOpenEvent = ({ buttonCopyAsString }) => {
    sendOphanReminderEvent('precontribution-reminder-prompt-clicked');
    sendOphanReminderEvent(`precontribution-reminder-prompt-copy-${buttonCopyAsString}`);
};
const wrapperMargins = css `
    margin: 18px 0;
`;
// TODO specify return type (need to update client to provide this first)
const buildPayload = (props) => {
    return {
        tracking: {
            ophanPageId: window.guardian.config.ophan.pageViewId,
            ophanComponentId: '',
            platformId: 'GUARDIAN_WEB',
            clientName: 'dcr',
            referrerUrl: window.location.origin + window.location.pathname,
        },
        targeting: {
            contentType: props.contentType,
            sectionName: props.sectionName || '',
            shouldHideReaderRevenue: props.shouldHideReaderRevenue,
            isMinuteArticle: props.isMinuteArticle,
            isPaidContent: props.isPaidContent,
            isSensitive: props.isSensitive,
            tags: props.tags,
            showSupportMessaging: !shouldHideSupportMessaging(props.isSignedIn || false),
            isRecurringContributor: isRecurringContributor(props.isSignedIn || false),
            lastOneOffContributionDate: getLastOneOffContributionDate(),
            epicViewLog: getViewLog(),
            weeklyArticleHistory: getWeeklyArticleHistory(),
            hasOptedOutOfArticleCount: hasOptedOutOfArticleCount(),
            mvtId: Number(getCookie('GU_mvt_id')),
            countryCode: props.countryCode,
        },
    };
};
const MemoisedInner = ({ isSignedIn, countryCode, contentType, sectionName, shouldHideReaderRevenue, isMinuteArticle, isPaidContent, isSensitive, tags, contributionsServiceUrl, }) => {
    const [Epic, setEpic] = useState();
    const [epicProps, setEpicProps] = useState();
    const [epicMeta, setEpicMeta] = useState();
    const [hasBeenSeen, setNode] = useHasBeenSeen({
        rootMargin: '-18px',
        threshold: 0,
        debounce: true,
    });
    useEffect(() => {
        const contributionsPayload = buildPayload({
            isSignedIn,
            countryCode,
            contentType,
            sectionName,
            shouldHideReaderRevenue,
            isMinuteArticle,
            isPaidContent,
            tags,
            contributionsServiceUrl,
            isSensitive,
        });
        window.guardian.automat = {
            react: React,
            preact: React,
            emotionCore,
            emotionTheming,
            emotion,
        };
        const dataPerf = initPerf('contributions-epic-data');
        dataPerf.start();
        getBodyEnd(contributionsPayload, `${contributionsServiceUrl}/epic?dataOnly=true`)
            .then((response) => {
            dataPerf.end();
            return checkForErrors(response);
        })
            .then((response) => response.json())
            .then((json) => {
            if (!json.data) {
                return;
            }
            const { meta, module } = json.data;
            const modulePerf = initPerf('contributions-epic-module');
            modulePerf.start();
            // eslint-disable-next-line no-restricted-globals
            window
                .guardianPolyfilledImport(module.url)
                .then((epicModule) => {
                modulePerf.end();
                setEpicMeta(meta);
                setEpicProps({
                    ...module.props,
                    onReminderOpen: sendOphanReminderOpenEvent,
                });
                setEpic(() => epicModule.ContributionsEpic); // useState requires functions to be wrapped
                sendOphanComponentEvent('INSERT', meta);
            })
                // eslint-disable-next-line no-console
                .catch((error) => console.log(`epic - error is: ${error}`));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Should only run once
    useEffect(() => {
        if (hasBeenSeen && epicMeta) {
            const { abTestName } = epicMeta;
            logView(abTestName);
            sendOphanComponentEvent('VIEW', epicMeta);
        }
    }, [hasBeenSeen, epicMeta]);
    if (Epic) {
        return (React.createElement("div", { ref: setNode, className: wrapperMargins },
            React.createElement(Epic, Object.assign({}, epicProps))));
    }
    return null;
};
export const SlotBodyEnd = ({ isSignedIn, countryCode, contentType, sectionName, shouldHideReaderRevenue, isMinuteArticle, isPaidContent, isSensitive, tags, contributionsServiceUrl, }) => {
    if (isSignedIn === undefined || countryCode === undefined) {
        return null;
    }
    if (shouldHideReaderRevenue || isPaidContent) {
        // We never serve Reader Revenue epics in this case
        return null;
    }
    // Memoised as we only ever want to call the Slots API once, for simplicity
    // and performance reasons.
    return (React.createElement(MemoisedInner, { isSignedIn: isSignedIn, countryCode: countryCode, contentType: contentType, sectionName: sectionName, shouldHideReaderRevenue: shouldHideReaderRevenue, isMinuteArticle: isMinuteArticle, isPaidContent: isPaidContent, isSensitive: isSensitive, tags: tags, contributionsServiceUrl: contributionsServiceUrl }));
};
